import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ColorRamp } from '../../types';

type Props = {
  initial: string;
  ramp: ColorRamp;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  src?: string | null;
  zoomable?: boolean;
};

const sizes = {
  sm: 'h-8 w-8 text-[12px]',
  md: 'h-11 w-11 text-[14px]',
  lg: 'h-16 w-16 text-[20px]',
  xl: 'h-24 w-24 text-[32px]',
} as const;

export function Avatar({ initial, ramp, size = 'md', src, zoomable }: Props) {
  const [zoomed, setZoomed] = useState(false);
  const canZoom = zoomable !== false && Boolean(src);

  const baseClasses = [
    'inline-flex items-center justify-center overflow-hidden rounded-full font-medium select-none',
    `ramp-${ramp}`,
    sizes[size],
    canZoom ? 'cursor-zoom-in active:scale-[0.97] transition' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const open = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!canZoom) return;
    e.stopPropagation();
    e.preventDefault();
    setZoomed(true);
  };

  const inner = src ? (
    <img src={src} alt="" className="h-full w-full object-cover pointer-events-none" draggable={false} />
  ) : (
    <span className="bg-child-accent text-ink-inverse h-full w-full inline-flex items-center justify-center">
      {initial}
    </span>
  );

  return (
    <>
      {canZoom ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={initial}
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') open(e);
          }}
          className={baseClasses}
        >
          {inner}
        </span>
      ) : (
        <span className={baseClasses}>{inner}</span>
      )}
      {zoomed && src ? <AvatarLightbox src={src} alt={initial} onClose={() => setZoomed(false)} /> : null}
    </>
  );
}

function AvatarLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-6"
      role="dialog"
      aria-modal
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white active:scale-[0.96]"
      >
        <X size={20} />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[90vw] cursor-zoom-out rounded-2xl object-contain"
        draggable={false}
      />
    </div>,
    document.body,
  );
}
